import classNames from "classnames";
import { useWindowSize } from "hooks/useWindowSize";
import Async from "components/DesignSystem/AsyncComponents/Async";
import BareInput from "components/DesignSystem/Input/BareInput";
import { Spinner } from "components/DesignSystem/Loader/loader";
import Table from "components/DesignSystem/Table/V2/Table";
import { StartBookkeepingDateEmptyScreen } from "components/GeneralLedger/GeneralLedgerEmptyScreen";
import { ArrowRight } from "components/icons/ArrowRight";
import { ArrowDown } from "components/icons/ArrowDown";
import { Button } from "components/DesignSystem/Button/Button";
import Dropdown from "components/DesignSystem/Dropdown/Dropdown";
import { ExportOptions } from "components/ExportOptions/ExportOptions";
import { SelectFolderForUpload } from "components/SelectFolderForUpload";
import { SavedToInkleDocsModal } from "components/SavedToInkleDocsModal/SavedToInkleDocsModal";
import { AmountSuperScript } from "components/design/AmountSuperScript";
import { DragIcon } from "components/icons/DragIcon";
import { EditPencil } from "components/icons/EditPencil";
import { CrossIcon } from "components/icons/CrossIcon";
import { motion } from "framer-motion";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useModal } from "hooks/useModal";
import { useToast } from "hooks/useToast";
import { ReportsType } from "pages/Books/Reports/Reports";
import { useRef, useState } from "react";
import { useDrag, useDrop } from "react-dnd";
import {
  createColumnHelper,
  ExpandedState,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  useReactTable,
} from "react-table-8.10.7";
import { useLazyGetPreviewUrlQuery } from "store/apis/previewUrl";
import {
  useLazyDownloadForecastReportQuery,
  useGetForecastSettingsQuery,
  useUpdateForecastPlugMutation,
} from "store/apis/reports";
import { getCommonPinningStyles } from "utils/getCommonPinningStyles";
import { getPinnedShadowClass } from "utils/getPinnedShadowClass";
import { openLink } from "utils/openLink";
import { DOWNLOAD_FORMAT, DOWNLOAD_FORMAT_TYPES } from "constants/bookkeeping";
import { REPORT_TYPE } from "dictionaries";
import { BackendError } from "types/utils/error";
import { ReportFilterType } from "./ReportFilter";
import {
  AccountWithForecastMonths,
  useForecastData,
} from "./hook/useForecastData";
import { NewAssumptionSetModal } from "components/Forecast/NewAssumptionSetModal";
import { LightTick } from "components/icons/LightTick";

const PLUG_DND_TYPE = "FORECAST_PLUG_ROW";
const SCROLL_ZONE_TOP = 100;
const SCROLL_ZONE_BOTTOM = 60;
const SCROLL_SPEED = 8;

export const ForecastReportDownload = ({
  reportType,
  filter,
}: {
  reportType: ReportsType;
  filter: ReportFilterType;
}) => {
  const { uuid: groupId } = useCurrentGroupContext();
  const entityId = useCurrentEntityId();
  const { alertToast, successToast } = useToast();
  const [downloadReport, { isLoading: isDownloading, isFetching }] =
    useLazyDownloadForecastReportQuery();
  const [getPreviewUrl] = useLazyGetPreviewUrlQuery();
  const selectFolderModal = useModal();
  const savedToInkleDocsModal = useModal();
  const [selectedFormat, setSelectedFormat] = useState<DOWNLOAD_FORMAT_TYPES>();
  const [savedDocumentId, setSavedDocumentId] = useState<string>();

  const onDownload = async (
    downloadType: DOWNLOAD_FORMAT_TYPES,
    exportToMail?: boolean,
    folderId?: string
  ) => {
    try {
      const { document_id, excel_document_id } = await downloadReport({
        groupId,
        entityId,
        reportType,
        startDate: filter.START_DATE!,
        endDate: filter.END_DATE!,
        forecastStartDate: filter.FORECAST_START!,
        forecastEndDate: filter.FORECAST_END!,
        assumptionSetId: filter.FORECAST_SET_ID || null,
        accountingMethod: filter.ACCOUNTING_METHOD,
        showNonZeroAccounts: filter.HIDE_ZERO,
        action: downloadType,
        folderId,
        exportToMail,
        viewBy: filter.VIEW_BY || null,
      }).unwrap();

      if (folderId) {
        setSavedDocumentId(
          (downloadType === DOWNLOAD_FORMAT.DOWNLOAD_EXCEL
            ? excel_document_id
            : document_id)!
        );
        savedToInkleDocsModal.open();
        return;
      }

      if (!exportToMail) {
        const { download_url } = await getPreviewUrl({
          groupId,
          fileId:
            downloadType === DOWNLOAD_FORMAT.DOWNLOAD_EXCEL
              ? excel_document_id
              : document_id,
        }).unwrap();
        openLink(download_url);
        successToast({ message: "Report downloaded" });
      } else {
        successToast({ message: "Report will be sent to your email" });
      }
    } catch (error) {
      alertToast({
        message: (error as BackendError)?.data?.error?.message,
      });
    }
  };

  const isLoading = isDownloading || isFetching;

  const excelOptions = (
    <ExportOptions
      onLocalDownload={() => onDownload(DOWNLOAD_FORMAT.DOWNLOAD_EXCEL, false)}
      onMailExport={() => onDownload(DOWNLOAD_FORMAT.DOWNLOAD_EXCEL, true)}
      onSaveInInkleDocs={() => {
        setSelectedFormat(DOWNLOAD_FORMAT.DOWNLOAD_EXCEL);
        selectFolderModal.open();
      }}
    />
  );

  const { isMobile } = useWindowSize();
  const [isExcelOptionsOpen, setIsExcelOptionsOpen] = useState(false);

  const handleToggleExcelOptions = (event: Event) => {
    event.preventDefault();
    setIsExcelOptionsOpen((isOpen) => !isOpen);
  };

  return (
    <>
      <Dropdown.Root modal={false}>
        <Dropdown.Trigger asChild className="t-group" disabled={isLoading}>
          <div>
            <Button
              customType="primary"
              size="xsmall"
              onClick={(e) => e.stopPropagation()}
              isLoading={isLoading}
              disabled={isLoading}
            >
              <div className="t-flex t-items-center t-gap-1">
                Export
                <div className="group-data-state-open:-t-rotate-180 t-transform t-transition t-duration-300 t-ease-in-out">
                  <ArrowDown color="currentColor" strokeWidth="1.5" size="16" />
                </div>
              </div>
            </Button>
          </div>
        </Dropdown.Trigger>
        <Dropdown.Portal>
          <Dropdown.Content
            className="t-w-24 sm:t-w-auto sm:t-min-w-56"
            sideOffset={4}
            align="end"
            side="bottom"
          >
            {isMobile ? (
              <>
                <Dropdown.Item onSelect={handleToggleExcelOptions}>
                  <span className="t-flex t-w-full t-items-center t-justify-between">
                    Excel
                    <span
                      className={classNames(
                        "t-transition-transform t-duration-200",
                        { "t-rotate-180": isExcelOptionsOpen }
                      )}
                    >
                      <ArrowDown
                        color="currentColor"
                        strokeWidth="1.5"
                        size="16"
                      />
                    </span>
                  </span>
                </Dropdown.Item>
                {isExcelOptionsOpen && excelOptions}
              </>
            ) : (
              <>
                <Dropdown.Sub>
                  <Dropdown.SubTrigger className="t-outline-none hover:t-outline-none focus-visible:t-outline-none">
                    Excel
                  </Dropdown.SubTrigger>
                  <Dropdown.Portal>
                    <Dropdown.SubContent
                      sideOffset={10}
                      alignOffset={-5}
                      collisionPadding={16}
                    >
                      {excelOptions}
                    </Dropdown.SubContent>
                  </Dropdown.Portal>
                </Dropdown.Sub>
              </>
            )}
          </Dropdown.Content>
        </Dropdown.Portal>
      </Dropdown.Root>

      {selectFolderModal.isOpen && (
        <SelectFolderForUpload
          showModal={selectFolderModal.isOpen}
          closeModal={selectFolderModal.close}
          onUpload={(folderId) => onDownload(selectedFormat!, false, folderId)}
        />
      )}
      {savedToInkleDocsModal.isOpen && savedDocumentId && (
        <SavedToInkleDocsModal
          isOpen={savedToInkleDocsModal.isOpen}
          close={savedToInkleDocsModal.close}
          fileId={savedDocumentId}
          reportType={REPORT_TYPE[reportType]}
        />
      )}
    </>
  );
};

type Props = {
  reportType: ReportsType;
  filter: ReportFilterType;
};

const AmountCell = ({ amount }: { amount: number | null | undefined }) => {
  if (amount === null || amount === undefined) {
    return <div className="t-flex t-justify-end t-text-text-30">—</div>;
  }
  return (
    <div className="t-flex t-justify-end">
      <AmountSuperScript amount={Number(amount)} />
    </div>
  );
};

const ForecastAmountCell = ({
  amount,
}: {
  amount: number | null | undefined;
}) => {
  if (amount === null || amount === undefined) {
    return <div className="t-flex t-justify-end t-text-text-30">—</div>;
  }
  return (
    <div className="t-flex t-justify-end">
      <AmountSuperScript amount={Number(amount)} />
    </div>
  );
};

const PlugNameCell = ({
  name,
  onSave,
}: {
  name: string;
  onSave: (newName: string) => void;
}) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(name);

  const startEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    setDraft(name);
    setEditing(true);
  };

  const confirm = (e: React.MouseEvent) => {
    e.stopPropagation();
    const trimmed = draft.trim();
    if (trimmed) onSave(trimmed);
    setEditing(false);
  };

  const cancel = (e: React.MouseEvent) => {
    e.stopPropagation();
    setEditing(false);
  };

  if (editing) {
    return (
      <div
        className="t-flex t-items-center t-gap-1"
        onClick={(e) => e.stopPropagation()}
      >
        <BareInput
          autoFocus
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              const trimmed = draft.trim();
              if (trimmed) onSave(trimmed);
              setEditing(false);
            }
            if (e.key === "Escape") setEditing(false);
          }}
          customSize="small"
        />
        <Button
          size="xsmall"
          customType="ghost"
          onClick={confirm}
          className="t-text-green-70"
        >
          <LightTick />
        </Button>
        <Button
          size="xsmall"
          customType="ghost"
          onClick={cancel}
          className="t-text-text-30"
        >
          <CrossIcon width={14} height={14} />
        </Button>
      </div>
    );
  }

  return (
    <div className="t-flex t-items-center t-gap-1 t-group/plug">
      <span className="t-pl-1">{name}</span>
      <Button
        size="xsmall"
        customType="ghost"
        onClick={startEdit}
        type="button"
        className="t-opacity-0 group-hover/plug:t-opacity-100 t-transition-opacity t-text-text-30"
      >
        <EditPencil width={14} height={14} />
      </Button>
    </div>
  );
};

const DroppableRow = ({
  rowUuid,
  onPlugDrop,
  hasChildren,
  onExpandRequest,
  children,
  className,
  onClick,
  ...motionProps
}: {
  rowUuid: string;
  onPlugDrop: (parentUuid: string) => void;
  hasChildren: boolean;
  onExpandRequest: () => void;
  children: (isDropHovered: boolean) => React.ReactNode;
  className?: string;
  onClick?: () => void;
} & Omit<React.ComponentProps<typeof motion.tr>, "children">) => {
  const ref = useRef<HTMLTableRowElement>(null);
  const hasChildrenRef = useRef(hasChildren);
  hasChildrenRef.current = hasChildren;
  const onExpandRequestRef = useRef(onExpandRequest);
  onExpandRequestRef.current = onExpandRequest;
  const expandedRef = useRef(false);
  const expandTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [{ isOver, canDrop }, drop] = useDrop(
    () => ({
      accept: PLUG_DND_TYPE,
      drop: () => onPlugDrop(rowUuid),
      hover: () => {
        if (!hasChildrenRef.current || expandedRef.current) return;
        if (expandTimerRef.current) return;
        expandTimerRef.current = setTimeout(() => {
          expandedRef.current = true;
          onExpandRequestRef.current();
          expandTimerRef.current = null;
        }, 450);
      },
      collect: (monitor) => ({
        isOver: monitor.isOver(),
        canDrop: monitor.canDrop(),
      }),
    }),
    [rowUuid, onPlugDrop]
  );

  const isDropHovered = isOver && canDrop;

  if (!isDropHovered) {
    expandedRef.current = false;
    if (expandTimerRef.current) {
      clearTimeout(expandTimerRef.current);
      expandTimerRef.current = null;
    }
  }

  drop(ref);

  return (
    <motion.tr
      ref={ref}
      onClick={onClick}
      className={classNames(className, {
        "!t-bg-purple-0": isDropHovered,
      })}
      {...motionProps}
    >
      {children(isDropHovered)}
    </motion.tr>
  );
};

const DraggablePlugRow = ({
  children,
  className,
  scrollContainerRef,
  ...motionProps
}: {
  children: React.ReactNode;
  className?: string;
  scrollContainerRef: React.RefObject<HTMLDivElement | null>;
} & React.ComponentProps<typeof motion.tr>) => {
  const ref = useRef<HTMLTableRowElement>(null);
  const rafRef = useRef<number | null>(null);
  const clientYRef = useRef<number | null>(null);

  const onDragOverRef = useRef((e: DragEvent) => {
    clientYRef.current = e.clientY;
  });

  const stopScrollRef = useRef(() => {
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    clientYRef.current = null;
    document.removeEventListener("dragover", onDragOverRef.current, true);
    document.removeEventListener("dragend", stopScrollRef.current, true);
    document.removeEventListener("drop", stopScrollRef.current, true);
  });

  const scrollLoopRef = useRef<(() => void) | undefined>(undefined);
  scrollLoopRef.current = () => {
    const el = scrollContainerRef.current;
    const y = clientYRef.current;
    if (el && y !== null) {
      const { top, bottom } = el.getBoundingClientRect();
      if (y < top + SCROLL_ZONE_TOP) {
        el.scrollTop -= SCROLL_SPEED;
      } else if (y > bottom - SCROLL_ZONE_BOTTOM) {
        el.scrollTop += SCROLL_SPEED;
      }
    }
    rafRef.current = requestAnimationFrame(() => scrollLoopRef.current?.());
  };

  const [{ isDragging }, drag] = useDrag(
    () => ({
      type: PLUG_DND_TYPE,
      item: () => {
        if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
        document.addEventListener("dragover", onDragOverRef.current, true);
        document.addEventListener("dragend", stopScrollRef.current, true);
        document.addEventListener("drop", stopScrollRef.current, true);
        rafRef.current = requestAnimationFrame(() => scrollLoopRef.current?.());
        return {};
      },
      end: () => stopScrollRef.current(),
      collect: (monitor) => ({ isDragging: monitor.isDragging() }),
    }),
    []
  );

  drag(ref);

  return (
    <motion.tr
      ref={ref}
      className={classNames(className, {
        "t-opacity-50 !t-cursor-grabbing": isDragging,
        "t-cursor-grab": !isDragging,
      })}
      {...motionProps}
    >
      {children}
    </motion.tr>
  );
};

const DESKTOP_PINNING = { left: ["name"] };
// On phones a pinned name column fills the screen, so everything scrolls together instead.
const NO_PINNING = { left: [] };

export const ForecastComparisonReport = ({ reportType, filter }: Props) => {
  const entityId = useCurrentEntityId();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [expanded, setExpanded] = useState<ExpandedState>({});
  const [ruleTarget, setRuleTarget] = useState<{
    uuid: string;
    name: string;
  } | null>(null);

  const { alertToast } = useToast();

  const { data: assumptionSets = [] } = useGetForecastSettingsQuery(
    { entityId },
    { skip: !entityId }
  );

  const [updatePlug] = useUpdateForecastPlugMutation();

  const {
    accounts,
    actualsKeys,
    forecastKeys,
    overlapKeys,
    isFetching,
    isSuccess,
  } = useForecastData({
    reportType,
    filter,
    forecast: {
      forecastStartDate: filter.FORECAST_START,
      forecastEndDate: filter.FORECAST_END,
      activeSetId: filter.FORECAST_SET_ID,
    },
  });

  const editingSet =
    assumptionSets.find((s) => s.uuid === filter.FORECAST_SET_ID) ??
    assumptionSets[0] ??
    null;

  const handlePlugNameSave = async (newName: string) => {
    if (!editingSet) return;
    try {
      await updatePlug({
        entityId,
        settingsId: editingSet.uuid,
        name: newName,
      }).unwrap();
    } catch (error) {
      alertToast({
        message:
          (error as BackendError)?.data?.error?.message ??
          "Failed to rename plug",
      });
    }
  };

  const handlePlugDrop = async (parentUuid: string) => {
    if (!editingSet) return;
    try {
      await updatePlug({
        entityId,
        settingsId: editingSet.uuid,
        parent_category_uuid: parentUuid,
      }).unwrap();
    } catch (error) {
      alertToast({
        message:
          (error as BackendError)?.data?.error?.message ??
          "Failed to move plug",
      });
    }
  };

  const openRule = (e: React.MouseEvent, uuid: string, name: string) => {
    e.stopPropagation();
    setRuleTarget({ uuid, name });
  };

  const columnHelper = createColumnHelper<AccountWithForecastMonths>();

  const nameColumn = columnHelper.accessor("name", {
    header: "Category",
    size: 300,
    cell: ({ row, getValue }) => {
      const name = getValue();
      const isPlug = Boolean(row.original.is_forecast_plug);

      return (
        <div
          style={{ paddingLeft: `${row.depth * 1}rem` }}
          className="t-flex t-items-center t-gap-1"
        >
          {isPlug ? (
            <span className="t-text-text-30 t-flex t-items-center">
              <DragIcon />
            </span>
          ) : (
            <div
              className={classNames({
                "t-rotate-90 t-transform t-transition t-duration-300 t-ease-in-out":
                  row.getIsExpanded(),
                "t-invisible": !row.getCanExpand(),
              })}
            >
              <ArrowRight stroke="1.5" color="currentColor" />
            </div>
          )}

          {isPlug ? (
            <PlugNameCell name={name} onSave={handlePlugNameSave} />
          ) : (
            <span>{name}</span>
          )}

          {!isPlug && row.original.identifier != null && (
            <div className="t-pl-1">
              <Button
                size="xsmall"
                onClick={(e) => openRule(e, row.original.uuid, name)}
                type="button"
                className="t-opacity-0 group-hover:t-opacity-100 t-transition-opacity"
              >
                ƒ
              </Button>
            </div>
          )}
        </div>
      );
    },
  });

  const pureActualsColumns = actualsKeys
    .filter((k) => !overlapKeys.has(k))
    .map((key) =>
      columnHelper.group({
        id: `group_actuals_${key}`,
        header: () => (
          <div className="t-flex t-justify-end t-w-full">{key}</div>
        ),
        columns: [
          columnHelper.accessor("uuid", {
            id: `actuals_${key}`,
            size: 114,
            header: () => (
              <div className="t-flex t-justify-end t-w-full">Actuals</div>
            ),
            cell: ({ row }) => (
              <AmountCell
                amount={row.original.monthData?.[key]?.summary_amount}
              />
            ),
          }),
        ],
      })
    );

  const overlapColumns = forecastKeys
    .filter((k) => overlapKeys.has(k))
    .map((key) =>
      columnHelper.group({
        id: `group_overlap_${key}`,
        header: () => (
          <div className="t-flex t-justify-end t-w-full">{key}</div>
        ),
        columns: [
          columnHelper.accessor("uuid", {
            id: `overlap_actual_${key}`,
            size: 114,
            header: () => (
              <div className="t-flex t-justify-end t-w-full">Actuals</div>
            ),
            cell: ({ row }) => (
              <AmountCell
                amount={row.original.monthData?.[key]?.actual_amount}
              />
            ),
          }),
          columnHelper.accessor("uuid", {
            id: `overlap_forecast_${key}`,
            size: 114,
            header: () => (
              <div className="t-flex t-justify-end t-w-full ">Forecast</div>
            ),
            cell: ({ row }) => (
              <ForecastAmountCell
                amount={row.original.monthData?.[key]?.summary_amount}
              />
            ),
          }),
        ],
      })
    );

  const pureForecastColumns = forecastKeys
    .filter((k) => !overlapKeys.has(k))
    .map((key) =>
      columnHelper.group({
        id: `group_forecast_${key}`,
        header: () => (
          <div className="t-flex t-justify-end t-w-full ">{key}</div>
        ),
        columns: [
          columnHelper.accessor("uuid", {
            id: `forecast_${key}`,
            size: 114,
            header: () => (
              <div className="t-flex t-justify-end t-w-full ">Forecast</div>
            ),
            cell: ({ row }) => (
              <ForecastAmountCell
                amount={row.original.monthData?.[key]?.summary_amount}
              />
            ),
          }),
        ],
      })
    );

  const columns = [
    nameColumn,
    ...pureActualsColumns,
    ...overlapColumns,
    ...pureForecastColumns,
  ];

  const { isMobile } = useWindowSize();

  const table = useReactTable({
    data: accounts,
    columns,
    state: {
      expanded,
      columnPinning: isMobile ? NO_PINNING : DESKTOP_PINNING,
    },
    onExpandedChange: setExpanded,
    getSubRows: (row) => row.types,
    getRowId: (row) => row.uuid,
    getExpandedRowModel: getExpandedRowModel(),
    getCoreRowModel: getCoreRowModel(),
  });

  const headerGroups = table.getHeaderGroups();
  const lastGroupIndex = headerGroups.length - 1;

  return (
    <>
      <Async.Root
        isEmpty={!isFetching && accounts.length === 0}
        isLoading={isFetching}
        isSuccess={isSuccess}
        renderOnLoad={
          <div className="t-flex t-flex-col t-items-center t-justify-center t-gap-5 t-py-48">
            <Spinner />
            <span className="t-text-body-sm t-text-text-30">
              Generating forecast report, this might take a while…
            </span>
          </div>
        }
      >
        <Async.Empty>
          <StartBookkeepingDateEmptyScreen />
        </Async.Empty>
        <Async.Success>
          <div className="t-pb-20 t-relative">
            <div
              ref={scrollContainerRef}
              className="t-overflow-auto scrollbar-visible t-max-h-[calc(100vh-300px)] t-w-full t-pb-24 sm:t-max-h-none"
            >
              <Table.Container sticky className="t-w-full">
                <Table.Content className="t-w-full">
                  <Table.Head>
                    {headerGroups.map((headerGroup, groupIndex) => {
                      const isLeafRow = groupIndex === lastGroupIndex;
                      const isGroupRow = !isLeafRow;

                      return (
                        <Table.HeadRow key={headerGroup.id}>
                          {headerGroup.headers.map((header) => {
                            const styles = getCommonPinningStyles(
                              header.column
                            );
                            const { boxShadow } = getPinnedShadowClass(
                              header.column
                            );
                            const isPinned = header.column.getIsPinned();
                            const colId = header.column.id;

                            return (
                              <Table.HeadCell
                                isUpperCase={false}
                                key={header.id}
                                colSpan={header.colSpan}
                                style={{
                                  ...styles,
                                  minWidth: isGroupRow
                                    ? undefined
                                    : header.column.getSize(),
                                }}
                                className={classNames(
                                  "!t-text-subtext-sm",
                                  boxShadow,
                                  {
                                    "!t-bg-surface-lighter-grey": isPinned,
                                    "t-drop-shadow-border-shadow-neutral-0":
                                      colId === "name",
                                    "!t-z-[3]": colId === "name" && isPinned,
                                    "!t-border-r-0.5 !t-border-r-neutral-10":
                                      colId !== "name" && !header.isPlaceholder,
                                    "t-border-b t-border-neutral-0": isGroupRow,
                                  }
                                )}
                              >
                                {header.isPlaceholder
                                  ? null
                                  : flexRender(
                                      header.column.columnDef.header,
                                      header.getContext()
                                    )}
                              </Table.HeadCell>
                            );
                          })}
                        </Table.HeadRow>
                      );
                    })}
                  </Table.Head>

                  <Table.Body>
                    {table.getRowModel().rows.map((row) => {
                      const isPlug = Boolean(row.original.is_forecast_plug);

                      const rowClassName = classNames(
                        "t-select-none t-px-3 t-text-body t-group t-border t-border-b t-border-solid t-border-t-0 t-border-x-0 t-border-neutral-0",
                        {
                          "t-text-text-100": row.depth === 0,
                          "t-text-text-60": row.depth === 1,
                          "t-text-text-30": row.depth >= 2,
                          "t-cursor-pointer hover:t-bg-surface-lighter-grey":
                            !isPlug && row.getCanExpand(),
                        }
                      );

                      const motionSharedProps = {
                        "aria-label": row.original.name,
                        layout: true as const,
                        initial: { opacity: 0 },
                        animate: { opacity: 1 },
                        exit: { opacity: 0 },
                        transition: {
                          duration: 0.4,
                          ease: "easeInOut" as const,
                        },
                      };

                      const buildCells = (isDropHovered: boolean) =>
                        row.getVisibleCells().map((cell) => {
                          const styles = getCommonPinningStyles(cell.column);
                          const { boxShadow } = getPinnedShadowClass(
                            cell.column
                          );
                          const colId = cell.column.id;
                          const isPinned = cell.column.getIsPinned();
                          const isForecastCol =
                            colId.startsWith("forecast_") ||
                            colId.startsWith("overlap_forecast_");

                          return (
                            <Table.Cell
                              key={cell.id}
                              style={{
                                ...styles,
                                minWidth: cell.column.getSize(),
                                ...(colId === "name"
                                  ? { width: "100px", minWidth: "100px" }
                                  : {}),
                              }}
                              className={classNames(boxShadow, {
                                "t-drop-shadow-border-shadow-neutral-0":
                                  colId === "name",
                                "!t-z-[1]": colId === "name" && !isPinned,
                                "!t-z-[3]": colId === "name" && isPinned,
                                "!t-border-r-0.5 !t-border-r-neutral-10":
                                  colId !== "name",
                                "!t-bg-purple-0": isDropHovered,
                                "!t-bg-green-0":
                                  !isDropHovered && isForecastCol,
                                "!t-bg-white":
                                  !isDropHovered &&
                                  !isForecastCol &&
                                  colId !== "name",
                                "!t-bg-surface":
                                  !isDropHovered && colId === "name",
                              })}
                            >
                              {flexRender(
                                cell.column.columnDef.cell,
                                cell.getContext()
                              )}
                            </Table.Cell>
                          );
                        });

                      if (isPlug) {
                        return (
                          <DraggablePlugRow
                            key={row.id}
                            {...motionSharedProps}
                            scrollContainerRef={scrollContainerRef}
                            className={rowClassName}
                          >
                            {buildCells(false)}
                          </DraggablePlugRow>
                        );
                      }

                      return (
                        <DroppableRow
                          key={row.id}
                          {...motionSharedProps}
                          rowUuid={row.original.uuid}
                          onPlugDrop={handlePlugDrop}
                          hasChildren={row.getCanExpand()}
                          onExpandRequest={() =>
                            !row.getIsExpanded() && row.toggleExpanded()
                          }
                          onClick={() => row.toggleExpanded()}
                          className={rowClassName}
                        >
                          {buildCells}
                        </DroppableRow>
                      );
                    })}
                  </Table.Body>
                </Table.Content>
              </Table.Container>
            </div>
          </div>
        </Async.Success>
      </Async.Root>

      {ruleTarget && (
        <NewAssumptionSetModal
          isOpen
          onClose={() => setRuleTarget(null)}
          editingSet={editingSet}
          highlightedCategoryUuid={ruleTarget.uuid}
        />
      )}
    </>
  );
};
